import { useCallback } from 'react';
import { useTranslations } from 'use-intl';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { PendingEmailImportsQueue } from '@/components/messages/PendingEmailImportsQueue';
import type { EmailThreadCompanyConnectionWithCompany } from '@/types/emailThread';

interface PendingEmailImportsAllCompaniesProps {
  /** Opens the given thread in the main Messages pane — see
   * {@link PendingEmailImportRow}'s doc comment for why review always routes through here. */
  onOpenThread: (threadId: string) => void;
}

/**
 * Platform-admin-view counterpart to {@link PendingEmailImports} — before this existed, a
 * platform admin in platform-admin view could already *read* an imported thread's content
 * (`has_email_thread_access()`'s platform-admin branch) but had no way to approve/reject it,
 * since `PendingEmailImports` only renders when a specific company is selected (issue #290
 * follow-up, found during manual testing). Fed by `listAllPendingConnections` (every company's
 * queue), with a company name badge per row so admins know which company each row belongs to.
 * See {@link PendingEmailImportsQueue}.
 */
export function PendingEmailImportsAllCompanies({ onOpenThread }: PendingEmailImportsAllCompaniesProps) {
  const t = useTranslations('consultantMessages.imports');
  const load = useCallback(() => ServiceRegistry.getEmailThreadService().listAllPendingConnections(), []);

  return (
    <PendingEmailImportsQueue<EmailThreadCompanyConnectionWithCompany>
      load={load}
      titleKey="pendingAllTitle"
      companyNameOf={(connection) => connection.companies?.name ?? t('unknownCompany')}
      onOpenThread={onOpenThread}
    />
  );
}
